<template>
  <div class="root-container">
    <div class="table-container">
    <!-- 搜索栏（固定） -->
    <div class="search-container" :class="{ 'mobile-search-top': !isDesktop }">
      <!-- 电脑端使用 Element UI 搜索框 -->
      <div v-if="isDesktop" class="desktop-search-wrapper">
        <div class="search-row">
          <el-input 
            v-model="searchKeyword" 
            placeholder="请输入关键词搜索" 
            clearable
            size="small"
            class="search-input"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
          <div class="button-group">
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-filter" 
              @click="toggleFilter"
              class="filter-button"
            >
              {{ isFilterOpen ? '收起筛选' : '展开筛选' }}
            </el-button>
          </div>
        </div>
        
        <!-- 电脑端筛选面板 -->
        <div v-if="isFilterOpen" class="filter-panel">
          <div class="filter-row">
            <div class="filter-item">
              <label>分类：</label>
              <el-select v-model="filterForm.category" placeholder="请选择分类" clearable size="small" style="width: 150px;">
                <el-option v-for="item in categoryOptions" :key="item.id" :label="item.name" :value="item.id">
                </el-option>
              </el-select>
            </div>
          </div>
          <div class="filter-actions">
            <div class="applied-filters">
              <span v-if="appliedFilterForm.category" class="filter-tag">
                分类: {{ getCategoryName(appliedFilterForm.category) }}
              </span>
              <span v-if="!appliedFilterForm.category" class="filter-tag filter-tag-empty">
                当前无筛选条件
              </span>
            </div>
            <div class="filter-buttons">
              <el-button size="small" @click="resetFilter">重置筛选</el-button>
              <el-button type="primary" size="small" @click="applyFilter">应用筛选</el-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 手机端使用 Vant 搜索框 -->
      <div v-else class="mobile-search-wrapper">
        <van-search
          v-model="searchKeyword"
          placeholder="请输入关键词搜索"
          show-action
          @search="handleSearch"
          @cancel="onSearchCancel"
          :placeholder-style="{ fontSize: '16px' }"
          :input-align="'center'"
        >
          <template #action>
            <div @click="handleSearch" style="margin-right: 10px; font-size: 16px; color: var(--primary); font-weight: 600;">搜索</div>
          </template>
        </van-search>
        
        <!-- 手机端筛选面板 -->
        <div v-if="isFilterOpen" class="mobile-filter-panel">
          <!-- 手机端已应用筛选条件显示 -->
          <div class="mobile-applied-filters">
            <div v-if="appliedFilterForm.category" class="applied-title">已应用筛选条件：</div>
            <div class="applied-tags">
              <span v-if="appliedFilterForm.category" class="mobile-filter-tag">
                分类: {{ getCategoryName(appliedFilterForm.category) }}
              </span>
              <span v-if="!appliedFilterForm.category" class="mobile-filter-tag empty">
                当前无筛选条件
              </span>
            </div>
          </div>
          
          <van-cell title="分类" is-link @click="showCategoryPicker = true">{{ getCategoryName(filterForm.category) || '全部' }}</van-cell>
          <div class="mobile-filter-actions">
            <van-button type="default" size="small" @click="resetFilter">重置</van-button>
            <van-button type="primary" size="small" @click="applyFilter">应用</van-button>
          </div>
        </div>
        
        <!-- 手机端筛选展开按钮 -->
        <div class="mobile-filter-toggle" @click="toggleFilter">
          <van-icon :name="isFilterOpen ? 'arrow-up' : 'arrow-down'" /> 
          <span>{{ isFilterOpen ? '收起筛选' : '展开筛选' }}</span>
        </div>
      </div>
    </div>
    
    <!-- 内容区域（可滚动） -->
    <div class="content-container">
      
      <!-- 电脑端表格 -->
      <div class="table-wrapper" v-if="isDesktop">
        <el-table 
          :data="filteredData" 
          style="width: 100%" 
          tooltip-effect="dark" 
          empty-text="暂无数据"
          class="desktop-table"
          :height="tableHeight"
        >
          <el-table-column label="分类" prop="name" align="left" width="100px"></el-table-column>
          <el-table-column label="字典" prop="data" align="left">
            <template slot-scope="scope">
              <div style="width:100%" class="data-box">
                <div class="data-tag" v-for="(value, key) in scope.row.data" :key="key">
                  {{ value.name }}
                  <i class="el-icon-circle-close" @click="handleRemove(scope.row, value.id)"></i>
                </div>
                <el-input class="input-new-tag" v-if="scope.row.add" v-model="scope.row.add_name"
                  :ref="'saveTagInput_' + scope.row.id" size="mini" @keyup.enter.native="handleInputConfirm(scope.row)"
                  @blur="handleInputConfirm(scope.row)"></el-input>
                <el-button v-else class="button-new-tag" size="mini" @click="showInput(scope.row)">+ 添加</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- 手机端卡片 -->
      <div v-else class="mobile-cards">
        <div 
          v-for="item in filteredData" 
          :key="item.id" 
          class="mobile-card"
        >
          <div class="card-header">
            <h3>{{ item.name }}</h3>
          </div>
          <div class="card-content">
            <div class="data-box-mobile">
              <div class="data-tag-mobile" v-for="(value, key) in item.data" :key="key">
                {{ value.name }}
                <i class="el-icon-circle-close" @click="handleRemove(item, value.id)"></i>
              </div>
            </div>
          </div>
          <div class="card-actions">
            <el-input class="input-new-tag" v-if="item.add" v-model="item.add_name"
              :ref="'saveTagInput_' + item.id" size="mini" @keyup.enter.native="handleInputConfirm(item)"
              @blur="handleInputConfirm(item)"></el-input>
            <van-button v-else
              type="primary" 
              size="small" 
              plain
              icon="plus"
              @click="showInput(item)"
            >
              添加
            </van-button>
          </div>
        </div>
        
        <!-- 手机端空状态 -->
        <div v-if="filteredData.length === 0" class="mobile-empty">
          暂无数据
        </div>
      </div>
    </div>
    
    <!-- 分页（固定） -->
    <div class="pagination-container">
    </div>
    
    <!-- 手机端选择器弹窗 -->
    <van-popup v-model="showCategoryPicker" position="bottom" get-container="body">
      <van-picker
        show-toolbar
        :columns="categoryColumns"
        @confirm="onCategoryConfirm"
        @cancel="showCategoryPicker = false"
      />
    </van-popup>
  </div>
</div>
</template>

<script>
// 静态导入 Vant 弹框组件
import { Dialog, Toast, Icon } from 'vant'
import {
  getTypeList,
  getStatusList,
  getOriginList,
  getMaturityList,
  getSectorList,
  getTagList,
  getWealthList,
  getCharacterList,
  addType,
  addStatus,
  addMaturity,
  addOrigin,
  addSector,
  addTag,
  addWealth,
  addCharacter,
  deleteType,
  deleteStatus,
  deleteOrigin,
  deleteMaturity,
  deleteSector,
  deleteTag,
  deleteCharacter,
  deleteWealth
} from "@/api/customer.js"

export default {
  name: "newindex",
  components: { [Icon.name]: Icon },
  data() {
    return {
      // 搜索相关
      searchKeyword: '',
      isDesktop: true,
      // 筛选相关
      isFilterOpen: false,
      filterForm: {
        category: ''
      },
      // 保存已应用的筛选条件
      appliedFilterForm: {
        category: ''
      },
      // 手机端选择器
      showCategoryPicker: false,
      categoryColumns: ['全部', '类别', '状态', '来源', '合作意愿', '行业', '标签', '财富', '性格'],
      categoryOptions: [
        { id: 'type', name: '类别' },
        { id: 'status', name: '状态' },
        { id: 'origin', name: '来源' },
        { id: 'maturity', name: '合作意愿' },
        { id: 'sector', name: '行业' },
        { id: 'tag', name: '标签' },
        { id: 'wealth', name: '财富' },
        { id: 'character', name: '性格' }
      ],
      // 表格数据
      list: [
        {
          id: 'type',
          name: '类别',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'status',
          name: '状态',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'origin',
          name: '来源',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'maturity',
          name: '合作意愿',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'sector',
          name: '行业',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'tag',
          name: '标签',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'wealth',
          name: '财富',
          data: [],
          add: false,
          add_name: ''
        },
        {
          id: 'character',
          name: '性格',
          data: [],
          add: false,
          add_name: ''
        }
      ],
    };
  },
  computed: {
    filteredData() {
      return this.list.filter(item => {
        // 关键词搜索
        const keyword = this.searchKeyword.toLowerCase()
        const matchesKeyword = !keyword || item.name.toLowerCase().includes(keyword)
        
        // 分类筛选
        const matchesCategory = !this.filterForm.category || item.id === this.filterForm.category
        
        return matchesKeyword && matchesCategory
      })
    },
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      // 计算表格高度：减去顶部栏、筛选区域、分页栏和底部的高度
      const headerHeight = 60 // 顶部栏高度
      const searchHeight = this.isFilterOpen ? 180 : 60 // 搜索栏高度（包含筛选面板）
      const paginationHeight = 60 // 分页栏高度
      const footerHeight = 40 // 底部高度
      const padding = 20 // 页面内边距
      return window.innerHeight - headerHeight - searchHeight - paginationHeight - footerHeight - padding - 100 // 100是其他元素的额外高度
    }
  },
  methods: {
    init() {
      this.getTypes()
      this.getStatus()
      this.getOrigin()
      this.getMaturity()
      this.getSector()
      this.getTag()
      this.getWealth()
      this.getCharacter()
    },
    getTypes() {
      getTypeList().then(res => {
        if (res.code === 200) {
          this.list[0].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getStatus() {
      getStatusList().then(res => {
        if (res.code === 200) {
          this.list[1].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getOrigin() {
      getOriginList().then(res => {
        if (res.code === 200) {
          this.list[2].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getMaturity() {
      getMaturityList().then(res => {
        if (res.code === 200) {
          this.list[3].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getSector() {
      getSectorList().then(res => {
        if (res.code === 200) {
          this.list[4].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getTag() {
      getTagList().then(res => {
        if (res.code === 200) {
          this.list[5].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getWealth() {
      getWealthList().then(res => {
        if (res.code === 200) {
          this.list[6].data = res.data
          this.$forceUpdate()
        }
      })
    },
    getCharacter() {
      getCharacterList().then(res => {
        if (res.code === 200) {
          this.list[7].data = res.data
          this.$forceUpdate()
        }
      })
    },

    showInput(e) {
      let _this = this
      const index = this.list.findIndex(item => item.id === e.id)
      if (index !== -1) {
        this.list[index].add = true
        this.$nextTick(_ => {
          const refName = 'saveTagInput_' + e.id
          if (this.$refs[refName] && this.$refs[refName][0] && this.$refs[refName][0].$refs.input) {
            this.$refs[refName][0].$refs.input.focus();
          }
        });
      }
      this.$forceUpdate()
    },
    handleInputConfirm(e) {
      let _this = this
      const index = this.list.findIndex(item => item.id === e.id)
      if (index !== -1) {
        if (e.add_name !== '') {
          const addFuncMap = {
            type: addType,
            status: addStatus,
            maturity: addMaturity,
            origin: addOrigin,
            sector: addSector,
            tag: addTag,
            wealth: addWealth,
            character: addCharacter
          }
          const getFuncMap = {
            type: this.getTypes,
            status: this.getStatus,
            origin: this.getOrigin,
            maturity: this.getMaturity,
            sector: this.getSector,
            tag: this.getTag,
            wealth: this.getWealth,
            character: this.getCharacter
          }
          const addFunc = addFuncMap[e.id]
          const getFunc = getFuncMap[e.id]
          if (addFunc && getFunc) {
            addFunc({ 'name': e.add_name }).then(res => {
              if (res.code === 200) {
                getFunc()
                this.list[index].add_name = ''
              }
            })
          }
        }
        this.list[index].add = false
      }
      this.$forceUpdate()
    },
    handleRemove(e, id) {
      let _this = this
      this.$confirm('此操作将永久删除, 是否继续?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const deleteFuncMap = {
          type: deleteType,
          status: deleteStatus,
          origin: deleteOrigin,
          maturity: deleteMaturity,
          sector: deleteSector,
          tag: deleteTag,
          wealth: deleteWealth,
          character: deleteCharacter
        }
        const getFuncMap = {
          type: this.getTypes,
          status: this.getStatus,
          origin: this.getOrigin,
          maturity: this.getMaturity,
          sector: this.getSector,
          tag: this.getTag,
          wealth: this.getWealth,
          character: this.getCharacter
        }
        const deleteFunc = deleteFuncMap[e.id]
        const getFunc = getFuncMap[e.id]
        if (deleteFunc && getFunc) {
          deleteFunc(id).then(res => {
            if (res.code === 200) {
              getFunc()
              _this.$forceUpdate()
              _this.$message({
                type: 'success',
                message: '删除成功!'
              });
            }
          })
        }
      }).catch(() => {

      });
    },
    handleSearch() {
      // 搜索逻辑已在computed中处理
    },
    // 检查屏幕大小
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    // 处理窗口大小变化
    handleResize() {
      this.checkScreenSize()
    },
    // 切换筛选面板
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen
    },
    // 应用筛选
    applyFilter() {
      // 保存当前筛选条件为已应用条件
      this.appliedFilterForm = JSON.parse(JSON.stringify(this.filterForm))
    },
    // 重置筛选
    resetFilter() {
      // 初始化筛选条件
      const initialFilter = {
        category: ''
      }
      // 同时重置当前筛选表单和已应用筛选条件
      this.filterForm = JSON.parse(JSON.stringify(initialFilter))
      this.appliedFilterForm = JSON.parse(JSON.stringify(initialFilter))
    },
    // 手机端分类选择确认
    onCategoryConfirm(value) {
      const categoryMap = {
        '全部': '',
        '类别': 'type',
        '状态': 'status',
        '来源': 'origin',
        '合作意愿': 'maturity',
        '行业': 'sector',
        '标签': 'tag',
        '财富': 'wealth',
        '性格': 'character'
      }
      this.filterForm.category = categoryMap[value] || ''
      this.showCategoryPicker = false
      this.applyFilter()
    },
    // 获取分类名称
    getCategoryName(categoryId) {
      if (!categoryId) return ''
      const category = this.categoryOptions.find(item => item.id === categoryId)
      return category ? category.name : ''
    },
    // 处理 Vant 搜索框取消事件
    onSearchCancel() {
      this.searchKeyword = ''
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.init()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
/* 使用全局样式中定义的 CSS 变量 */
.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 搜索栏（固定） */
.search-container {
  margin-bottom: 0;
  padding: 20px;
  background-color: var(--background);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  transition: all 0.3s ease;
}

/* 手机端搜索置顶 */
.mobile-search-top {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  padding: 10px 15px;
}

/* 电脑端搜索容器 */
.desktop-search-wrapper {
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.search-input {
  width: 100%;
  max-width: 400px;
  font-size: var(--font-size-sm);
}

.filter-button {
  white-space: nowrap;
}

/* 按钮组样式 */
.button-group {
  display: flex;
  align-items: center;
}

/* 筛选面板 */
.filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  padding: 15px;
  margin-top: 10px;
  box-shadow: var(--shadow);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 筛选操作区域 */
.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--border);
}

/* 已应用筛选标签容器 */
.applied-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

/* 单个筛选标签样式 */
.filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  display: inline-flex;
  align-items: center;
}

/* 空状态筛选标签 */
.filter-tag-empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

/* 筛选按钮容器 */
.filter-buttons {
  display: flex;
  gap: 10px;
}

.filter-item label {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text);
}

/* 手机端已应用筛选条件样式 */
.mobile-applied-filters {
  padding: 10px 15px;
  background-color: var(--bg-100);
  border-radius: var(--border-radius);
  margin-bottom: 15px;
}

.mobile-applied-filters .applied-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
}

.mobile-applied-filters .applied-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  display: inline-block;
}

.mobile-filter-tag.empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

/* 手机端筛选操作按钮区域 */
.mobile-filter-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 0;
  margin-top: 10px;
  border-top: 1px solid var(--border);
}

/* 手机端搜索容器 */
.mobile-search-wrapper {
  width: 100%;
}

/* 手机端筛选面板 */
.mobile-filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  margin-top: 10px;
  overflow: hidden;
}

/* 手机端筛选展开按钮 */
.mobile-filter-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  margin-top: 10px;
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: var(--text-200);
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-filter-toggle:hover {
  background-color: var(--border-light);
}

/* 内容区域（可滚动） */
.content-container {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 20px 0;
  padding-bottom: 0;
}

/* 电脑端表格 */
.table-wrapper {
  height: 100%;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  font-size: 14px;
}

.desktop-table {
  height: 100%;
  margin-bottom: 0;
  font-size: 14px;
}

/* 手机端卡片 */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

.mobile-card {
  border: 1px solid var(--border);
  border-radius: var(--border-radius-lg);
  padding: 15px;
  background-color: var(--bg-200);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.mobile-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

.card-header h3 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-content {
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  flex-wrap: wrap;
  align-items: center;
}

/* Vant按钮样式优化 */
.card-actions .van-button {
  font-size: 13px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

/* 手机端空状态 */
.mobile-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--text-200);
  font-size: var(--font-size-sm);
}

/* 适配小屏幕，确保操作按钮不会换行 */
@media (max-width: 375px) {
  .card-actions {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
  
  .card-actions .van-button {
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }
}

/* 分页（固定） */
.pagination-container {
  margin-top: 0;
  margin-bottom: 0;
  padding: 10px;
  border-top: 1px solid var(--border);
  text-align: right;
  box-sizing: border-box;
}

/* 字典数据样式 */
.data-box {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.data-tag {
  position: relative;
  box-sizing: border-box;
  padding: 0 10px;
  padding-right: 20px;
  height: 32px;
  font-size: 12px;
  text-align: center;
  line-height: 30px;
  border-collapse: separate;
  white-space: nowrap;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  margin-right: 10px;
  margin-bottom: 10px;
  border-color: rgba(0, 0, 0, 0);
}

.data-tag:hover {
  background-color: white;
  border-color: #DCDFE6;
}

.data-tag:hover .el-icon-circle-close {
  display: block;
}

.data-tag .el-icon-circle-close {
  position: absolute;
  display: none;
  right: 4px;
  top: 9px;
  font-size: 14px;
  cursor: pointer;
}

/* 手机端字典数据样式 */
.data-box-mobile {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.data-tag-mobile {
  position: relative;
  box-sizing: border-box;
  padding: 0 10px;
  padding-right: 25px;
  height: 32px;
  font-size: 12px;
  text-align: center;
  line-height: 30px;
  border-collapse: separate;
  white-space: nowrap;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  border-color: rgba(0, 0, 0, 0);
  background-color: white;
  border-color: #DCDFE6;
}

.data-tag-mobile .el-icon-circle-close {
  position: absolute;
  right: 4px;
  top: 9px;
  font-size: 14px;
  cursor: pointer;
  display: block;
}

.input-new-tag {
  width: 90px;
  margin-right: 10px;
  margin-bottom: 10px;
  vertical-align: bottom;
}

.button-new-tag {
  margin-right: 10px;
  margin-bottom: 10px;
  height: 32px;
  line-height: 30px;
  padding-top: 0;
  padding-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-container .search-input {
    display: none;
  }
  
  .content-container {
    padding: 10px 0;
  }
  
  .search-input {
    max-width: 100%;
  }
  
  .pagination-container {
    text-align: center;
    padding: 15px;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .filter-item {
    width: 100%;
  }
  
  .filter-item .el-select,
  .filter-item .el-input-number {
    width: 100% !important;
  }
}

@media (min-width: 769px) {
  .search-container .mobile-search-container,
  .mobile-filter-toggle {
    display: none;
  }
  
  .desktop-table {
    display: block;
  }
  
  .mobile-cards {
    display: none;
  }
}

@media (max-width: 768px) {
  .search-container{
    width: 100vw;
    margin-left: -15px;
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    background-color: var(--bg-200);
  }
  .van-search{
    padding:0;
  }
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: flex;
  }
  
  .search-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-input {
    max-width: 100%;
  }
}
</style>